/**
 * App-wide design tokens.
 *
 * Sprint 3 / #15. The codebase has ~1,700 inline `style={{ ... }}` props
 * with hard-coded colours / radii / spacing. Migrating all of them is out
 * of scope for one sprint, but every new component MUST consume tokens
 * from this file (or AntD's theme via `ConfigProvider`) instead of hard-
 * coding values. See THEMING.md for the rollout plan.
 *
 * The token names mirror the AntD seed-token vocabulary so we can plug
 * the same object straight into `<ConfigProvider theme={{ token }}>`.
 */
import type { ThemeConfig } from 'antd';

export const palette = {
  // Brand
  primary:        '#6366f1',
  primaryHover:   '#4f46e5',

  // Semantic
  success:        '#22c55e',
  warning:        '#f59e0b',
  error:          '#ef4444',
  info:           '#0ea5e9',

  // Neutrals
  text:           '#1f2937',
  textSecondary:  '#4b5563',
  textMuted:      '#6b7280',
  border:         '#e5e7eb',
  borderSubtle:   '#f3f4f6',
  bgBase:         '#ffffff',
  bgLayout:       '#f9fafb',
  bgElevated:     '#ffffff',

  // Domain accents — used by tutorial categories etc. Centralizing them
  // here means a re-skin only touches this file.
  category: {
    gettingStarted:    '#6366f1',
    propertyAdmin:     '#0ea5e9',
    residentMgmt:      '#14b8a6',
    financial:         '#22c55e',
    operations:        '#f97316',
    governance:        '#8b5cf6',
    communication:     '#ec4899',
    selfService:       '#0ea5e9',
    sysAdmin:          '#ef4444',
  },
} as const;

export const radii = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
} as const;

export const spacing = {
  xs:  4,
  sm:  8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
} as const;

export const shadows = {
  sm: '0 1px 2px rgba(0,0,0,0.04)',
  md: '0 4px 12px rgba(0,0,0,0.08)',
  lg: '0 12px 32px rgba(0,0,0,0.12)',
} as const;

/** Drop into `<ConfigProvider theme={antdTheme}>` at the app root. */
export const antdTheme: ThemeConfig = {
  token: {
    colorPrimary:    palette.primary,
    colorSuccess:    palette.success,
    colorWarning:    palette.warning,
    colorError:      palette.error,
    colorInfo:       palette.info,
    colorTextBase:   palette.text,
    colorBgBase:     palette.bgBase,
    colorBgLayout:   palette.bgLayout,
    colorBorder:     palette.border,
    borderRadius:    radii.md,
    fontFamily:      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  components: {
    Button: { borderRadius: radii.md, controlHeight: 36 },
    Card:   { borderRadiusLG: radii.lg, paddingLG: spacing.xl },
    Input:  { borderRadius: radii.md },
    Modal:  { borderRadiusLG: radii.lg },
    Tag:    { borderRadiusSM: radii.sm },
  },
};

/* ============================================================================
 * ATRIUM design tokens (Sprint 82, ADR-082).
 *
 * The "Atrium" visual redesign — corporate-light indigo-blue system with a
 * two-tier nav and Salesforce-Lightning-style role homes. These tokens are
 * ADDITIVE: the legacy `palette`/`antdTheme` above still drive the current UI.
 * The new Atrium shell (gated by the `atriumRedesign` flag) consumes
 * `atriumTokens` / `atriumAntdTheme`, and the same `--*` values are also
 * exposed as CSS custom properties in
 * `src/pages/condoManagement/styles/atrium-tokens.css`.
 *
 * Brand decision (ADR-082): keep CondoPro name + "C" logo; adopt only
 * Atrium's visual design. Values are the exact `:root` set from
 * `design_handoff_condo_workspace/prototype/console/theme2.jsx`.
 * ==========================================================================*/

export const atriumTokens = {
  // Brand / accent
  blue:     '#2563EB', // --blue
  blueD:    '#1D4FD7', // --blue-d (hover/pressed)
  blueS:    '#EAF0FE', // --blue-s (soft fill)
  blueS2:   '#F3F7FF', // --blue-s2 (softer fill)
  blueInk:  '#1B3A8C', // --blue-ink (deep brand text)

  // Text
  ink:      '#18223A', // --ink   (primary text)
  ink2:     '#37425C', // --ink2  (secondary text)
  mut:      '#6B7488', // --mut   (muted)
  mut2:     '#6A7180', // --mut2  (most muted / placeholders) — AA: 4.9:1 on #FFFFFF (95.2)

  // Surfaces / lines
  bg:       '#F4F7FC', // --bg    (app background)
  panel:    '#F7F9FD', // --panel (context panel)
  card:     '#FFFFFF', // --card  (cards / surfaces)
  hover:    '#F2F5FB', // --hover (row/item hover)
  line:     '#E8ECF3', // --line  (hairline border)
  line2:    '#DCE2EC', // --line2 (stronger border)

  // Status (color + soft tint pairs)
  ok:       '#1E9E5B', okS:     '#E4F5EC',
  amber:    '#D9870B', amberS:  '#FBF0DC',
  red:      '#D8453C', redS:    '#FBE9E8',
  violet:   '#6D4AE0', violetS: '#EFEAFC',
  teal:     '#0C8C9A', tealS:   '#DEF1F3',

  // Gradients
  gradGreeting:  'linear-gradient(118deg,#1E51C8,#2563EB 52%,#3B74F0)',
  gradHero:      'linear-gradient(150deg,#1E51C8,#3B74F0)',
  gradAssistant: 'linear-gradient(140deg,#6D4AE0,#2563EB)',

  // Radii — cards/banner 14–18, controls 9–11, pills 20–999
  radius:        14, // --r
  radiusCard:    14,
  radiusBanner:  18, // greeting/hero
  radiusControl: 10,
  radiusControlSm: 9,
  radiusPill:    999,

  // Shadows
  sh:  '0 1px 2px rgba(20,30,70,.05)',   // --sh
  sh2: '0 6px 22px rgba(20,30,80,.10)',  // --sh2
  shc: '0 2px 8px rgba(37,99,235,.10)',  // --shc (brand)

  // Layout dimensions (px) — from README Design Tokens
  rail:        84,
  panelWidth:  236,
  rightRail:   340,
  topBar:      62,

  // Typography
  fontFamily: "'Onest', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
} as const;

/** Drop into `<ConfigProvider theme={atriumAntdTheme}>` when the flag is on. */
export const atriumAntdTheme: ThemeConfig = {
  token: {
    colorPrimary:       atriumTokens.blue,
    colorPrimaryHover:  atriumTokens.blueD,
    colorSuccess:       atriumTokens.ok,
    colorWarning:       atriumTokens.amber,
    colorError:         atriumTokens.red,
    colorInfo:          atriumTokens.blue,
    colorTextBase:      atriumTokens.ink,
    colorText:          atriumTokens.ink,
    colorTextSecondary: atriumTokens.ink2,
    colorTextTertiary:  atriumTokens.mut,
    colorTextQuaternary: atriumTokens.mut2,
    colorBgBase:        atriumTokens.card,
    colorBgContainer:   atriumTokens.card,
    colorBgLayout:      atriumTokens.bg,
    colorBgElevated:    atriumTokens.card,
    colorBorder:        atriumTokens.line2,
    colorBorderSecondary: atriumTokens.line,
    borderRadius:       atriumTokens.radiusControl,
    borderRadiusLG:     atriumTokens.radiusCard,
    borderRadiusSM:     atriumTokens.radiusControlSm,
    boxShadow:          atriumTokens.sh,
    boxShadowSecondary: atriumTokens.sh2,
    fontFamily:         atriumTokens.fontFamily,
    // Sprint 89D — CRITICAL modal-close fix. Under React 19 + antd 6.2.3 the
    // bundled @rc-component/motion CSSMotion freezes the modal LEAVE transition
    // at `…-leave-start` (never reaches `-leave-active`, animationName resolves
    // to none → `animationend` never fires → the dialog node is never
    // unmounted). Net effect: NO modal could be closed (X / Cancel / Esc / mask
    // / even programmatic open=false all stuck), blocking every write workflow.
    // Setting the supported `motion` seed token to false makes antd's
    // MotionWrapper pass {motion:false} to the CSSMotion Context, which skips
    // the transition and toggles visibility synchronously — modals open AND
    // close reliably. Scoped to the Atrium theme only (flag-gated rollout);
    // legacy theme untouched. Trade-off: AntD enter/leave animations are off
    // under Atrium — acceptable for a B2B tool and snappier; revisit if a
    // future antd patch fixes the React-19 leave-transition upstream.
    //
    // Sprint 94 (finding 94.2) re-check -- DECISION: KEEP. Installed antd is
    // still 6.2.3 (package.json "^6.2.3"; node_modules resolves to 6.2.3), the
    // exact version that exhibits the rc-component/motion React-19 LEAVE freeze;
    // no upstream patch has shipped that we could pin to, and the freeze can't
    // be safely re-tested in this edit-only sweep. Removing `motion:false` risks
    // re-introducing the un-closable-modal bug that blocks every write workflow,
    // so the global motion-disable STANDS pending a verified upstream fix.
    //
    // a11y follow-up (Sprint 95.5): a `prefers-reduced-motion` gate -- re-enable
    // animations only for users who have NOT requested reduced motion -- is the
    // planned next step. NOT implemented here: turning motion back ON for most
    // users would re-expose the modal-LEAVE freeze, so it must land together
    // with a verified rc-motion/antd fix, not before.
    motion: false,
  },
  components: {
    Button: { borderRadius: atriumTokens.radiusControl, controlHeight: 40, primaryShadow: atriumTokens.shc, fontWeight: 700 },
    Card:   { borderRadiusLG: atriumTokens.radiusCard, paddingLG: 20 },
    Input:  { borderRadius: atriumTokens.radiusControl, controlHeight: 40 },
    Select: { borderRadius: atriumTokens.radiusControl, controlHeight: 40 },
    Modal:  { borderRadiusLG: atriumTokens.radiusBanner },
    Tag:    { borderRadiusSM: atriumTokens.radiusPill },
    Table:  { headerColor: atriumTokens.mut, headerBg: atriumTokens.card, rowHoverBg: atriumTokens.hover, borderColor: atriumTokens.line },
  },
};
